import { useEffect, useState, useRef } from "react";
import api from "../api/api";
import {
  Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Button, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Tooltip, CircularProgress, Chip
} from "@mui/material";
import AddIcon          from "@mui/icons-material/Add";
import EditIcon         from "@mui/icons-material/Edit";
import DeleteIcon       from "@mui/icons-material/Delete";
import UploadFileIcon   from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useAuth } from "../components/AuthContext";

const baseURL = process.env.REACT_APP_API_URL || "";

const ROWS_OPTIONS = [10, 25, 50];

export default function Puestos() {
  const { hasPermission } = useAuth();
  const [data, setData]           = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit]   = useState(false);
  const [selected, setSelected]   = useState(null);
  const [form, setForm]           = useState({ nombre_puesto: "", descripcion: "" });
  const [pagina, setPagina]       = useState(0);
  const [rowsPorPagina, setRowsPorPagina] = useState(10);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const docInputRef = useRef();

  const cargar = async () => {
    try {
      const res = await api.get("/puestos");
      setData(res.data);
      setPagina(0);
    } catch (error) {
      console.error("Error loading positions:", error);
    }
  };

  useEffect(() => { cargar(); }, []);

  const handleCreate = async () => {
    if (!form.nombre_puesto.trim()) return alert("Enter the position name");
    try {
      await api.post("/puestos", form);
      setOpenCreate(false);
      setForm({ nombre_puesto: "", descripcion: "" });
      cargar();
    } catch (error) {
      alert("Error creating position: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdate = async () => {
    if (!form.nombre_puesto.trim()) return alert("Enter the position name");
    try {
      await api.put(`/puestos/${selected.id_puesto}`, form);
      setOpenEdit(false);
      setSelected(null);
      setForm({ nombre_puesto: "", descripcion: "" });
      cargar();
    } catch (error) {
      alert("Error updating position: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this position?")) return;
    try {
      await api.delete(`/puestos/${id}`);
      cargar();
    } catch (error) {
      alert("Error deleting position: " + (error.response?.data?.detail || error.message));
    }
  };

  const openEditDialog = (p) => {
    setSelected(p);
    setForm({ nombre_puesto: p.nombre_puesto || "", descripcion: p.descripcion || "" });
    setOpenEdit(true);
  };

  const handleDocUpload = async (e, puesto) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const formData = new FormData();
    formData.append("file", file);
    setUploadingDoc(puesto.id_puesto);
    try {
      await api.post(`/puestos/${puesto.id_puesto}/documento`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      cargar();
    } catch (err) {
      alert("Error uploading document: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingDoc(null);
    }
  };

  const paginados = data.slice(pagina * rowsPorPagina, (pagina + 1) * rowsPorPagina);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Positions</Typography>
        {hasPermission("PUESTOS_CREAR") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ nombre_puesto: "", descripcion: "" }); setOpenCreate(true); }}>
            Create Position
          </Button>
        )}
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Position Name</TableCell>
            <TableCell sx={{ color: "white" }}>Description</TableCell>
            <TableCell sx={{ color: "white" }}>Position Description</TableCell>
            <TableCell sx={{ color: "white" }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginados.map(p => (
            <TableRow key={p.id_puesto} hover>
              <TableCell>{p.nombre_puesto}</TableCell>
              <TableCell>{p.descripcion || "—"}</TableCell>
              <TableCell>
                {p.doc_url
                  ? (
                    <Chip
                      icon={<PictureAsPdfIcon />}
                      label="View PDF"
                      size="small"
                      color="error"
                      variant="outlined"
                      clickable
                      component="a"
                      href={`${baseURL}/${p.doc_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  )
                  : <Typography variant="body2" color="text.secondary">No document</Typography>
                }
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                  {hasPermission("PUESTOS_EDITAR") && (
                    <Tooltip title={p.doc_url ? "Replace PDF" : "Upload PDF"}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={uploadingDoc === p.id_puesto}
                          onClick={() => { docInputRef.current._puesto = p; docInputRef.current.click(); }}
                        >
                          {uploadingDoc === p.id_puesto
                            ? <CircularProgress size={16} />
                            : <UploadFileIcon fontSize="small" />
                          }
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {hasPermission("PUESTOS_EDITAR") && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasPermission("PUESTOS_ELIMINAR") && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(p.id_puesto)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {data.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 4 }}>
                No positions registered
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {data.length > 0 && (
        <TablePagination
          component="div"
          count={data.length}
          page={pagina}
          onPageChange={(_, p) => setPagina(p)}
          rowsPerPage={rowsPorPagina}
          onRowsPerPageChange={e => { setRowsPorPagina(parseInt(e.target.value)); setPagina(0); }}
          rowsPerPageOptions={ROWS_OPTIONS}
          labelRowsPerPage="Rows:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      )}

      {/* Hidden input for uploading PDF */}
      <input
        type="file"
        accept="application/pdf"
        ref={docInputRef}
        style={{ display: "none" }}
        onChange={e => handleDocUpload(e, docInputRef.current._puesto)}
      />

      {/* Create */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Position</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Position Name" value={form.nombre_puesto}
            onChange={e => setForm({ ...form, nombre_puesto: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Description" value={form.descripcion}
            onChange={e => setForm({ ...form, descripcion: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Position</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Position Name" value={form.nombre_puesto}
            onChange={e => setForm({ ...form, nombre_puesto: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Description" value={form.descripcion}
            onChange={e => setForm({ ...form, descripcion: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>Update</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
